import React, { useContext } from "react";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Typography,
  TextField,
  Button,
  Container
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useChangePassword } from "../../hooks/api/useProfile";
import {
  useKivunovaTranslation,
  localizedPath,
  KivuI18nContext,
  useToggleVisibility,
  useResponsive,
  BRAND_COLOR,
  BRAND_HOVER,
  BRAND_DISABLED
} from "@kivunova/kivufrontendcommon";
import { PasswordFormData } from "./types/profileTypes";
import { useNavigate } from "react-router-dom";
import PageActionBar from "../common/responsive/PageActionBar";
import { PORTAL_PREFIX } from "@kivunova/kivufrontendcommon";
import PasswordVisibilityAdornment from "../common/PasswordVisibilityAdornment/PasswordVisibilityAdornment";

const PasswordChangeForm: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const changePassword = useChangePassword();
  // useResponsive replaces both useTheme() + useMediaQuery()
  // isCompact = mobile | tablet (matches old matchMD intent, plus touch-aware)
  // spacing.p  = 2 / 2.5 / 3 per form-factor (finer than old xs:2 sm:3 jump)
  const { isCompact, spacing } = useResponsive();
  const { language, defaultLang } = useContext(KivuI18nContext);
  const navigate = useNavigate();

  const {
    isVisible: isCurrentPasswordVisible,
    toggle: toggleCurrentPasswordVisibility
  } = useToggleVisibility(false);
  const {
    isVisible: isNewPasswordVisible,
    toggle: toggleNewPasswordVisibility
  } = useToggleVisibility(false);
  const {
    isVisible: isConfirmPasswordVisible,
    toggle: toggleConfirmPasswordVisibility
  } = useToggleVisibility(false);

  const schema = Yup.object({
    currentPassword: Yup.string().required(
      t("current-password-required", "Current password is required")
    ),
    newPassword: Yup.string()
      .required(t("new-password-required", "New password is required"))
      .min(8, t("new-password-min", "Password must be at least 8 characters")),
    confirmPassword: Yup.string()
      .required(t("confirm-password-required", "Please confirm your password"))
      .oneOf(
        [Yup.ref("newPassword")],
        t("passwords-must-match", "Passwords must match")
      )
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<PasswordFormData>({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: ""
    }
  });

  const onSubmit = async (data: PasswordFormData) => {
    try {
      await changePassword.mutateAsync({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword
      });
      reset();
      navigate(localizedPath(PORTAL_PREFIX, language, defaultLang));
    } catch (err) {
      console.error("Password change failed:", err);
    }
  };

  return (
    <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <Container
        maxWidth={false}
        disableGutters
        sx={{ width: "100%", display: "flex", justifyContent: "center" }}
      >
        <Card
          data-testid="profile-password-card"
          sx={{
            width: "100%",
            borderRadius: 3,
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            // spacing.p: mobile=2, tablet=2.5, desktop=3
            p: spacing.p
          }}
        >
          <CardContent>
            <Typography
              variant="h6"
              fontWeight={600}
              color="text.primary"
              sx={{ mb: 1 }}
            >
              {t("change-password-title", "Change Password")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3, maxWidth: 450 }}
            >
              {t(
                "change-password-description",
                "Update your account password below. Make sure your new password is strong and secure."
              )}
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              data-testid="profile-password-form"
              sx={{
                display: "flex",
                flexDirection: "column",
                pt: 3,
                gap: 3,
                // isCompact = mobile | tablet — full width on both.
                width: isCompact ? "100%" : "50%",
                margin: "auto"
              }}
            >
              <Controller
                name="currentPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type={isCurrentPasswordVisible ? "text" : "password"}
                    label={t("current-password", "Current Password")}
                    error={!!errors.currentPassword}
                    helperText={errors.currentPassword?.message}
                    InputProps={{
                      endAdornment: (
                        <PasswordVisibilityAdornment
                          isVisible={isCurrentPasswordVisible}
                          onToggle={toggleCurrentPasswordVisibility}
                          disabled={changePassword.isPending}
                        />
                      )
                    }}
                  />
                )}
              />

              <Controller
                name="newPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type={isNewPasswordVisible ? "text" : "password"}
                    label={t("new-password", "New Password")}
                    error={!!errors.newPassword}
                    helperText={errors.newPassword?.message}
                    InputProps={{
                      endAdornment: (
                        <PasswordVisibilityAdornment
                          isVisible={isNewPasswordVisible}
                          onToggle={toggleNewPasswordVisibility}
                          disabled={changePassword.isPending}
                        />
                      )
                    }}
                  />
                )}
              />

              <Controller
                name="confirmPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type={isConfirmPasswordVisible ? "text" : "password"}
                    label={t("confirm-password", "Confirm New Password")}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword?.message}
                    InputProps={{
                      endAdornment: (
                        <PasswordVisibilityAdornment
                          isVisible={isConfirmPasswordVisible}
                          onToggle={toggleConfirmPasswordVisibility}
                          disabled={changePassword.isPending}
                        />
                      )
                    }}
                  />
                )}
              />

              {/* PageActionBar handles mobile stacking, touch target sizing,
                  and gap — correct usage, no CSS class changes needed here */}
              <PageActionBar sx={{ mt: 2 }} fullWidth>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={changePassword.isPending}
                  data-testid="profile-password-submit"
                  sx={{
                    flex: 1,
                    textTransform: "none",
                    borderRadius: 2,
                    backgroundColor: BRAND_COLOR,
                    color: "white",
                    "&:hover": { backgroundColor: BRAND_HOVER },
                    "&:disabled": { backgroundColor: BRAND_DISABLED }
                  }}
                >
                  {t("change-password", "Change Password")}
                </LoadingButton>

                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => reset()}
                  disabled={changePassword.isPending}
                  sx={{
                    flex: 1,
                    textTransform: "none",
                    borderRadius: 2,
                    backgroundColor: "white",
                    color: BRAND_COLOR,
                    border: `1px solid ${BRAND_COLOR}`,
                    "&:hover": { backgroundColor: BRAND_HOVER, color: "white" }
                  }}
                >
                  {t("reset-form", "Reset")}
                </Button>
              </PageActionBar>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default PasswordChangeForm;
